// Global Exceptions

/**
 * Flatten API error payloads: top-level msg/errMsg/message, string `error`,
 * or nested `{ error: { message?, errors?: string[] } }` (automation-manager validation).
 */
export function extractResponseBodyErrorMessage(responseBody) {
  if (!responseBody || typeof responseBody !== 'object') {
    return null;
  }

  if (typeof responseBody.msg === 'string' && responseBody.msg.trim()) {
    return responseBody.msg.trim();
  }
  if (typeof responseBody.errMsg === 'string' && responseBody.errMsg.trim()) {
    return responseBody.errMsg.trim();
  }
  if (typeof responseBody.message === 'string' && responseBody.message.trim()) {
    return responseBody.message.trim();
  }
  if (typeof responseBody.error === 'string' && responseBody.error.trim()) {
    return responseBody.error.trim();
  }

  const nested = responseBody.error;
  if (nested && typeof nested === 'object') {
    const details = Array.isArray(nested.errors)
      ? nested.errors
          .filter((item) => typeof item === 'string' && item.trim())
          .map((item) => item.trim())
      : [];
    const summary = typeof nested.message === 'string' ? nested.message.trim() : '';

    if (summary && details.length > 0) {
      return `${summary}: ${details.join('; ')}`;
    }
    if (summary) {
      return summary;
    }
    if (details.length > 0) {
      return details.join('; ');
    }
  }

  return null;
}

export function resolveHttpErrorMessage({
  message,
  responseBody,
  responseCode,
} = {}) {
  const fromBody = extractResponseBodyErrorMessage(responseBody);
  if (fromBody) {
    return fromBody;
  }
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  if (responseCode === 401) {
    return 'Session expired';
  }
  if (responseCode === 403) {
    return "You don't have access";
  }
  return 'Request failed';
}

class HTTPError extends Error {
  constructor({
    message,
    responseCode,
    responseBody,
    responseHeaders,
    requestHeaders,
  }) {
    super(resolveHttpErrorMessage({ message, responseBody, responseCode }));
    this.name = 'HTTPError';
    this.responseCode = responseCode;
    this.responseHeaders = responseHeaders;
    this.requestHeaders = requestHeaders;
    this.responseBody = responseBody;
  }
}

class HTTPTimeoutError extends HTTPError {
  constructor({
    message,
    responseCode,
    responseBody,
    responseHeaders,
    requestHeaders,
  }) {
    super({
      message: message || 'Request timed out',
      responseCode,
      responseBody,
      responseHeaders,
      requestHeaders,
    });
    this.name = 'HTTPTimeoutError';
  }
}

// For HTTP 403
class HTTPForbidden extends HTTPError {
  constructor({
    message,
    responseCode,
    responseBody,
    responseHeaders,
    requestHeaders,
  }) {
    super({
      message,
      responseCode: responseCode ?? 403,
      responseBody,
      responseHeaders,
      requestHeaders,
    });
    this.name = 'HTTPForbidden';
  }
}

// For HTTP 401
class HTTPUnauthorized extends HTTPError {
  constructor({
    message,
    responseCode,
    responseBody,
    responseHeaders,
    requestHeaders,
  }) {
    super({
      message,
      responseCode: responseCode ?? 401,
      responseBody,
      responseHeaders,
      requestHeaders,
    });
    this.name = 'HTTPUnauthorized';
  }
}

export { HTTPError, HTTPForbidden, HTTPTimeoutError, HTTPUnauthorized };
